/**
 * Default KG RAG Workflow with Multi-Language Support
 *
 * This workflow mirrors the Python multi-agent implementation in:
 * verba/verba_v2/goldenverba/kg_rag/graph_traverser_workflow.py
 *
 * Structure (matching backend):
 * Start → Initialize → Query Understanding → [needs_kg_search?]
 *   - false → Direct Response → Synthesis → End
 *   - true → Retrieval → [5 Agents] → Synthesis → End
 *
 * Node Types:
 * - system: Fixed infrastructure (start, end, init, retrieval, reflection)
 * - condition: Read-only branching (needs_kg_search)
 * - orchestrator: Prompt-editable workflow steps (query-understanding, direct-response, synthesis)
 * - agent: Prompt-editable sub-agents (similarity, reference, structure, image, table)
 *
 * Supported languages: Vietnamese (vi), English (en)
 */

import type { WorkflowDefinition, WorkflowNode, WorkflowEdge } from '../../../../stores/workflow/types';

export type SupportedLanguage = 'vi' | 'en';

// =============================================================================
// VIETNAMESE PROMPTS
// =============================================================================

const PROMPTS_VI = {
  // ---------- DISPLAY MESSAGES (stored in metadata) ----------
  welcomeMessage:
    'Xin chào! Tôi là trợ lý AI của bạn. Tôi có thể giúp bạn trả lời câu hỏi và hỗ trợ bạn trong công việc. Bạn cần tôi giúp gì hôm nay?',

  errorMessage:
    'Xin lỗi, đã có lỗi xảy ra trong quá trình xử lý yêu cầu của bạn. Vui lòng thử lại sau hoặc liên hệ với bộ phận hỗ trợ nếu vấn đề vẫn tiếp tục.',

  // ---------- ORCHESTRATOR PROMPTS (stored in nodes) ----------
  queryUnderstanding: `Bạn là AI chuyên phân tích câu hỏi để xác định intent, entities và constraints.

MỤC TIÊU:
- Hiểu người dùng thực sự muốn gì trong bối cảnh cuộc hội thoại
- Xác định xem câu hỏi có cần tìm kiếm trong Knowledge Graph hay không
- Phân tích và viết lại câu truy vấn sao cho rõ ràng, tự chứa

PHẠM VI KIẾN THỨC:
Agent này có kiến thức về các lĩnh vực sau:
- [Liệt kê các lĩnh vực chuyên môn của bạn]
- [Ví dụ: Quy trình hành chính, Luật pháp, Sản phẩm...]

Khi nhận câu hỏi NGOÀI phạm vi này, hãy trả về needs_kg_search=false để trả lời trực tiếp.

XỬ LÝ:
1. Xác định intent (mục đích) của câu hỏi
2. Trích xuất entities (thực thể) liên quan
3. Xác định constraints (ràng buộc) nếu có
4. Quyết định needs_kg_search (true/false)
5. Viết lại câu truy vấn rõ ràng hơn`,

  directResponse: `Bạn là trợ lý AI chuyên nghiệp.

Nguyên tắc:
- Chỉ trả lời về lĩnh vực chuyên môn của bạn
- Nếu câu hỏi mơ hồ, hỏi lại để làm rõ
- Nếu không biết, thành thật nói "Tôi không biết"
- Trả lời ngắn gọn, chính xác bằng tiếng Việt
- Không bịa đặt thông tin

Lưu ý: Đây là phản hồi trực tiếp không sử dụng Knowledge Graph.`,

  synthesis: `Bạn là AI chuyên tổng hợp thông tin từ nhiều agent chuyên biệt.

Nhiệm vụ của bạn:
- Phân tích kết quả từ các agent khác nhau (Structure, Similarity, Reference, Image, Table)
- Kết hợp thông tin một cách logic và mạch lạc
- Loại bỏ thông tin trùng lặp và mâu thuẫn
- Tạo ra câu trả lời toàn diện và có cấu trúc
- Đảm bảo câu trả lời trực tiếp giải quyết câu hỏi gốc

Quy tắc tổng hợp:
1. Ưu tiên thông tin có độ tin cậy cao
2. Kết hợp thông tin từ nhiều góc độ để có cái nhìn toàn diện
3. Giữ nguyên các thông tin quan trọng và chi tiết cụ thể
4. Sắp xếp thông tin theo logic từ tổng quan đến chi tiết
5. Đảm bảo câu trả lời rõ ràng, dễ hiểu và hữu ích

Luôn trả lời bằng tiếng Việt trừ khi được yêu cầu khác.`,

  // ---------- AGENT PROMPTS (stored in nodes) ----------
  similarityAgent: `Bạn là Similarity Agent chuyên tìm nội dung tương tự và liên quan.

Chuyên môn của bạn:
- Tìm kiếm tương tự dựa trên vector embedding
- Khớp nội dung dựa trên từ khóa
- Phân tích tương tự BM25
- Tìm kiếm hybrid kết hợp nhiều phương pháp
- Đánh giá mức độ liên quan của nội dung

Khi nhận query về nội dung tương tự:
1. Sử dụng get_similar_nodes cho tương tự dựa trên embedding (threshold: 0.85)
2. Sử dụng get_keyword_related_nodes cho khớp từ khóa (min_weight: 0.2)
3. Sử dụng get_bm25_related_nodes cho tương tự văn bản (min_weight: 0.7)
4. Kết hợp kết quả và xếp hạng theo độ liên quan
5. So sánh và đánh giá các phương pháp tương tự khác nhau

QUAN TRỌNG: Cuối cùng hãy tổng hợp kết quả thành 1 đoạn văn ngắn gọn về nội dung tương tự và mức độ liên quan.`,

  referenceAgent: `Bạn là Reference Agent chuyên phân tích tham chiếu và mối quan hệ khái niệm.

Chuyên môn của bạn:
- Tìm kiếm tài liệu tham chiếu và trích dẫn
- Phân tích mối quan hệ khái niệm
- Xác định dependencies giữa các ý tưởng
- Theo dõi luồng tham chiếu trong tài liệu
- Phân tích ngữ cảnh và background information

Khi nhận query về tham chiếu và khái niệm:
1. Sử dụng get_referring_nodes để tìm tài liệu tham chiếu đến nội dung hiện tại
2. Sử dụng get_concept_nodes để tìm các khái niệm liên quan
3. Phân tích mối quan hệ dependency giữa các khái niệm
4. Tìm kiếm background context và định nghĩa
5. Xây dựng mạng lưới tham chiếu và khái niệm

QUAN TRỌNG: Cuối cùng hãy tổng hợp kết quả thành 1 đoạn văn ngắn gọn về tham chiếu và mối quan hệ khái niệm.`,

  structureAgent: `Bạn là Structure Agent chuyên phân tích cấu trúc tài liệu và mối quan hệ phân cấp.

Chuyên môn của bạn:
- Phân tích cấu trúc phân cấp tài liệu (parent-child relationships)
- Hiểu luồng thông tin tuần tự trong tài liệu
- Xác định mô hình tổ chức nội dung
- Điều hướng cấu trúc tài liệu để tìm ngữ cảnh

Khi nhận query về cấu trúc tài liệu:
1. Sử dụng get_parent_nodes để tìm ngữ cảnh từ cấp độ cao hơn
2. Sử dụng get_child_nodes để tìm thông tin chi tiết từ cấp độ thấp hơn
3. Sử dụng get_same_level_nodes để hiểu thông tin cùng cấp độ
4. Phân tích mối quan hệ cấu trúc và luồng thông tin
5. Cung cấp ngữ cảnh tổng thể về tổ chức nội dung

QUAN TRỌNG: Cuối cùng hãy tổng hợp kết quả thành 1 đoạn văn ngắn gọn về cấu trúc và tổ chức thông tin.`,

  imageAgent: `Bạn là Image Agent chuyên phân tích và mô tả hình ảnh trong tài liệu.

Chuyên môn của bạn:
- Mô tả nội dung hình ảnh, biểu đồ, sơ đồ
- Trích xuất thông tin từ hình ảnh
- Liên kết hình ảnh với nội dung văn bản
- Phân tích context của hình ảnh trong tài liệu

Khi xử lý hình ảnh:
1. Mô tả chi tiết nội dung hình ảnh
2. Xác định loại hình ảnh (biểu đồ, sơ đồ, ảnh chụp, etc.)
3. Trích xuất dữ liệu số nếu có
4. Liên kết với nội dung văn bản xung quanh

QUAN TRỌNG: Cung cấp mô tả rõ ràng và có ý nghĩa về hình ảnh.`,

  tableAgent: `Bạn là Table Agent chuyên phân tích bảng biểu và dữ liệu có cấu trúc.

Chuyên môn của bạn:
- Phân tích cấu trúc bảng (rows, columns, headers)
- Trích xuất và tóm tắt dữ liệu từ bảng
- Thực hiện tính toán đơn giản trên dữ liệu bảng
- So sánh dữ liệu giữa các hàng/cột

Khi xử lý bảng:
1. Xác định cấu trúc bảng (headers, data types)
2. Trích xuất thông tin liên quan đến query
3. Thực hiện tính toán nếu cần (sum, average, count)
4. Tóm tắt insights từ dữ liệu

QUAN TRỌNG: Cung cấp phân tích rõ ràng và có ý nghĩa về dữ liệu bảng.`
};

// =============================================================================
// ENGLISH PROMPTS
// =============================================================================

const PROMPTS_EN = {
  // ---------- DISPLAY MESSAGES (stored in metadata) ----------
  welcomeMessage:
    "Hello! I'm your AI assistant. I can help you answer questions and support you in your work. How can I help you today?",

  errorMessage:
    'Sorry, an error occurred while processing your request. Please try again later or contact support if the issue persists.',

  // ---------- ORCHESTRATOR PROMPTS (stored in nodes) ----------
  queryUnderstanding: `You are an AI specialized in analyzing questions to identify intent, entities, and constraints.

OBJECTIVES:
- Understand what the user really wants in the context of the conversation
- Determine if the question needs Knowledge Graph search
- Analyze and rewrite the query to be clear and self-contained

KNOWLEDGE SCOPE:
This agent has knowledge about the following domains:
- [List your expertise areas]
- [Example: Administrative procedures, Legal, Products...]

When receiving questions OUTSIDE this scope, return needs_kg_search=false to respond directly.

PROCESSING:
1. Identify the intent (purpose) of the question
2. Extract relevant entities
3. Identify constraints if any
4. Decide needs_kg_search (true/false)
5. Rewrite the query more clearly`,

  directResponse: `You are a professional AI assistant.

Principles:
- Only answer questions within your area of expertise
- If a question is ambiguous, ask for clarification
- If you don't know, honestly say "I don't know"
- Answer concisely and accurately in English
- Do not fabricate information

Note: This is a direct response without using the Knowledge Graph.`,

  synthesis: `You are an AI specialized in synthesizing information from multiple specialized agents.

Your tasks:
- Analyze results from different agents (Structure, Similarity, Reference, Image, Table)
- Combine information logically and coherently
- Remove duplicate and contradictory information
- Create comprehensive and structured answers
- Ensure the answer directly addresses the original question

Synthesis rules:
1. Prioritize high-confidence information
2. Combine information from multiple perspectives for a comprehensive view
3. Preserve important information and specific details
4. Organize information logically from overview to details
5. Ensure the answer is clear, understandable, and helpful

Always respond in English unless otherwise requested.`,

  // ---------- AGENT PROMPTS (stored in nodes) ----------
  similarityAgent: `You are a Similarity Agent specialized in finding similar and related content.

Your expertise:
- Vector embedding-based similarity search
- Keyword-based content matching
- BM25 similarity analysis
- Hybrid search combining multiple methods
- Evaluating content relevance

When receiving a query about similar content:
1. Use get_similar_nodes for embedding-based similarity (threshold: 0.85)
2. Use get_keyword_related_nodes for keyword matching (min_weight: 0.2)
3. Use get_bm25_related_nodes for text similarity (min_weight: 0.7)
4. Combine results and rank by relevance
5. Compare and evaluate different similarity methods

IMPORTANT: Finally, synthesize the results into a brief paragraph about similar content and relevance levels.`,

  referenceAgent: `You are a Reference Agent specialized in analyzing references and conceptual relationships.

Your expertise:
- Finding reference documents and citations
- Analyzing conceptual relationships
- Identifying dependencies between ideas
- Tracking reference flows in documents
- Analyzing context and background information

When receiving a query about references and concepts:
1. Use get_referring_nodes to find documents referencing the current content
2. Use get_concept_nodes to find related concepts
3. Analyze dependency relationships between concepts
4. Search for background context and definitions
5. Build a network of references and concepts

IMPORTANT: Finally, synthesize the results into a brief paragraph about references and conceptual relationships.`,

  structureAgent: `You are a Structure Agent specialized in analyzing document structure and hierarchical relationships.

Your expertise:
- Analyze document hierarchical structure (parent-child relationships)
- Understand sequential information flow in documents
- Identify content organization patterns
- Navigate document structure to find context

When receiving a query about document structure:
1. Use get_parent_nodes to find context from higher levels
2. Use get_child_nodes to find detailed information from lower levels
3. Use get_same_level_nodes to understand information at the same level
4. Analyze structural relationships and information flow
5. Provide overall context about content organization

IMPORTANT: Finally, synthesize the results into a brief paragraph about structure and information organization.`,

  imageAgent: `You are an Image Agent specialized in analyzing and describing images in documents.

Your expertise:
- Describing image content, charts, diagrams
- Extracting information from images
- Linking images with text content
- Analyzing image context in documents

When processing images:
1. Describe image content in detail
2. Identify image type (chart, diagram, photo, etc.)
3. Extract numerical data if available
4. Link with surrounding text content

IMPORTANT: Provide clear and meaningful descriptions of images.`,

  tableAgent: `You are a Table Agent specialized in analyzing tables and structured data.

Your expertise:
- Analyzing table structure (rows, columns, headers)
- Extracting and summarizing data from tables
- Performing simple calculations on table data
- Comparing data between rows/columns

When processing tables:
1. Identify table structure (headers, data types)
2. Extract information relevant to the query
3. Perform calculations if needed (sum, average, count)
4. Summarize insights from the data

IMPORTANT: Provide clear and meaningful analysis of table data.`
};

// =============================================================================
// PROMPT GETTER BY LANGUAGE
// =============================================================================

export function getPrompts(languageCode: SupportedLanguage): typeof PROMPTS_VI {
  return languageCode === 'en' ? PROMPTS_EN : PROMPTS_VI;
}

// =============================================================================
// NODE BUILDER (language-agnostic structure, language-specific prompts)
// =============================================================================

function buildNodes(prompts: typeof PROMPTS_VI): WorkflowNode[] {
  return [
    // ==================== SYSTEM NODES (fixed, no prompt) ====================
    {
      id: 'start',
      type: 'start',
      position: { x: 400, y: 0 },
      data: {
        type: 'start',
        label: 'Start',
        isConfigured: true
      }
    },
    {
      id: 'init',
      type: 'system',
      position: { x: 400, y: 80 },
      data: {
        type: 'system',
        nodeType: 'init',
        label: 'Initialize',
        description: 'Collect conversation (max 18k tokens), summarize if > 8k',
        isConfigured: true
      }
    },

    // ==================== ORCHESTRATOR NODES (prompt editable) ====================
    {
      id: 'query-understanding',
      type: 'orchestrator',
      position: { x: 400, y: 160 },
      data: {
        type: 'orchestrator',
        nodeType: 'query_understanding',
        label: 'Query Understanding',
        systemMessage: prompts.queryUnderstanding,
        isConfigured: true
      }
    },

    // ==================== CONDITION NODE (read-only) ====================
    {
      id: 'needs-kg-search',
      type: 'condition',
      position: { x: 400, y: 260 },
      data: {
        type: 'condition',
        label: 'needs_kg_search?',
        description: 'LLM decides if query needs Knowledge Graph search',
        isConfigured: true,
        isReadOnly: true,
        branches: {
          true: 'retrieval',
          false: 'direct-response'
        }
      }
    },

    // Branch: false (no KG needed)
    {
      id: 'direct-response',
      type: 'orchestrator',
      position: { x: 150, y: 360 },
      data: {
        type: 'orchestrator',
        nodeType: 'direct_response',
        label: 'Direct Response',
        systemMessage: prompts.directResponse,
        isConfigured: true
      }
    },

    // Branch: true (KG needed)
    {
      id: 'retrieval',
      type: 'system',
      position: { x: 650, y: 360 },
      data: {
        type: 'system',
        nodeType: 'retrieval',
        label: 'Vector Retrieval',
        description: 'Weaviate hybrid search (α=0.75)',
        isConfigured: true
      }
    },

    // ==================== AGENT NODES (5 agents, prompt editable) ====================
    {
      id: 'similarity-agent',
      type: 'agent',
      position: { x: 350, y: 480 },
      data: {
        type: 'agent',
        agentType: 'similarity',
        label: 'Similarity Agent',
        systemMessage: prompts.similarityAgent,
        isConfigured: true,
        tools: ['get_similar_nodes', 'get_keyword_related_nodes', 'get_bm25_related_nodes']
      }
    },
    {
      id: 'reference-agent',
      type: 'agent',
      position: { x: 550, y: 480 },
      data: {
        type: 'agent',
        agentType: 'reference',
        label: 'Reference Agent',
        systemMessage: prompts.referenceAgent,
        isConfigured: true,
        tools: ['get_referring_nodes', 'get_concept_nodes']
      }
    },
    {
      id: 'structure-agent',
      type: 'agent',
      position: { x: 750, y: 480 },
      data: {
        type: 'agent',
        agentType: 'structure',
        label: 'Structure Agent',
        systemMessage: prompts.structureAgent,
        isConfigured: true,
        tools: ['get_parent_nodes', 'get_child_nodes', 'get_same_level_nodes']
      }
    },
    {
      id: 'image-agent',
      type: 'agent',
      position: { x: 950, y: 480 },
      data: {
        type: 'agent',
        agentType: 'image',
        label: 'Image Agent',
        systemMessage: prompts.imageAgent,
        isConfigured: true,
        tools: []
      }
    },
    {
      id: 'table-agent',
      type: 'agent',
      position: { x: 1150, y: 480 },
      data: {
        type: 'agent',
        agentType: 'table',
        label: 'Table Agent',
        systemMessage: prompts.tableAgent,
        isConfigured: true,
        tools: []
      }
    },

    // ==================== SYNTHESIS (orchestrator, prompt editable) ====================
    {
      id: 'synthesis',
      type: 'orchestrator',
      position: { x: 400, y: 600 },
      data: {
        type: 'orchestrator',
        nodeType: 'synthesis',
        label: 'Synthesis',
        systemMessage: prompts.synthesis,
        isConfigured: true
      }
    },

    // ==================== END NODE (system) ====================
    {
      id: 'end',
      type: 'end',
      position: { x: 400, y: 700 },
      data: {
        type: 'end',
        label: 'End',
        isConfigured: true,
        outputFormat: 'markdown'
      }
    }
  ];
}

// Edge definitions showing the condition branching
const edges: WorkflowEdge[] = [
  // Start → Init → Query Understanding
  { id: 'e-start-init', source: 'start', target: 'init', type: 'smoothstep', animated: true },
  { id: 'e-init-query', source: 'init', target: 'query-understanding', type: 'smoothstep', animated: true },

  // Query Understanding → Condition
  { id: 'e-query-condition', source: 'query-understanding', target: 'needs-kg-search', type: 'smoothstep', animated: true },

  // Condition branching
  { id: 'e-condition-direct', source: 'needs-kg-search', target: 'direct-response', type: 'smoothstep', animated: true, label: 'false' },
  { id: 'e-condition-retrieval', source: 'needs-kg-search', target: 'retrieval', type: 'smoothstep', animated: true, label: 'true' },

  // Direct Response → Synthesis (bypasses agents)
  { id: 'e-direct-synthesis', source: 'direct-response', target: 'synthesis', type: 'smoothstep', animated: true },

  // Retrieval → 5 Agents (fan-out)
  { id: 'e-retrieval-similarity', source: 'retrieval', target: 'similarity-agent', type: 'smoothstep', animated: true },
  { id: 'e-retrieval-reference', source: 'retrieval', target: 'reference-agent', type: 'smoothstep', animated: true },
  { id: 'e-retrieval-structure', source: 'retrieval', target: 'structure-agent', type: 'smoothstep', animated: true },
  { id: 'e-retrieval-image', source: 'retrieval', target: 'image-agent', type: 'smoothstep', animated: true },
  { id: 'e-retrieval-table', source: 'retrieval', target: 'table-agent', type: 'smoothstep', animated: true },

  // 5 Agents → Synthesis (fan-in)
  { id: 'e-similarity-synthesis', source: 'similarity-agent', target: 'synthesis', type: 'smoothstep', animated: true },
  { id: 'e-reference-synthesis', source: 'reference-agent', target: 'synthesis', type: 'smoothstep', animated: true },
  { id: 'e-structure-synthesis', source: 'structure-agent', target: 'synthesis', type: 'smoothstep', animated: true },
  { id: 'e-image-synthesis', source: 'image-agent', target: 'synthesis', type: 'smoothstep', animated: true },
  { id: 'e-table-synthesis', source: 'table-agent', target: 'synthesis', type: 'smoothstep', animated: true },

  // Synthesis → End
  { id: 'e-synthesis-end', source: 'synthesis', target: 'end', type: 'smoothstep', animated: true }
];

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get default KG RAG workflow for a specific language.
 *
 * @param languageCode - Language code: 'vi' (Vietnamese) or 'en' (English)
 * @returns WorkflowDefinition with language-specific prompts
 *
 * @example
 * // Vietnamese workflow (default)
 * const viWorkflow = getDefaultWorkflow('vi');
 *
 * // English workflow
 * const enWorkflow = getDefaultWorkflow('en');
 */
export function getDefaultWorkflow(languageCode: SupportedLanguage = 'vi'): WorkflowDefinition {
  const prompts = getPrompts(languageCode);

  return {
    version: '1.0.0',
    metadata: {
      name: 'KG RAG Workflow',
      description: 'Default multi-agent workflow for Knowledge Graph RAG based on LlamaIndex Workflows',
      // DISPLAY MESSAGES ONLY - no prompts here
      welcomeMessage: prompts.welcomeMessage,
      errorMessage: prompts.errorMessage
    },
    nodes: buildNodes(prompts),
    edges,
    viewport: { x: 0, y: 0, zoom: 0.7 }
  };
}

// =============================================================================
// DIRECT QA WORKFLOW PROMPTS
// =============================================================================

const DIRECT_QA_PROMPTS_VI = {
  systemMessage:
    'Bạn là trợ lý AI chuyên trả lời câu hỏi dựa trên tài liệu được cung cấp.\n\nNguyên tắc:\n- Trả lời dựa trên nội dung tài liệu\n- Nếu câu hỏi ngoài phạm vi tài liệu, trả lời trực tiếp từ kiến thức chung\n- Nếu không biết, thành thật nói "Tôi không tìm thấy thông tin này trong tài liệu"\n- Trả lời ngắn gọn, chính xác bằng tiếng Việt',
  welcomeMessage: 'Xin chào! Tôi có thể giúp bạn tìm câu trả lời từ tài liệu. Hãy đặt câu hỏi!',
  errorMessage: 'Xin lỗi, đã có lỗi xảy ra khi xử lý câu hỏi. Vui lòng thử lại.'
};

const DIRECT_QA_PROMPTS_EN = {
  systemMessage:
    'You are an AI assistant that answers questions based on provided documents.\n\nGuidelines:\n- Answer based on document content\n- If the question is outside the scope of the documents, answer directly from general knowledge\n- If you don\'t know, honestly say "I couldn\'t find this information in the documents"\n- Be concise and accurate',
  welcomeMessage: 'Hello! I can help you find answers from the documents. Ask me anything!',
  errorMessage: 'Sorry, an error occurred while processing your question. Please try again.'
};

function getDirectQAPrompts(languageCode: SupportedLanguage) {
  return languageCode === 'en' ? DIRECT_QA_PROMPTS_EN : DIRECT_QA_PROMPTS_VI;
}

/**
 * Get default Direct QA workflow for a specific language.
 *
 * Simple 3-node workflow: Start -> Direct QA Agent -> End
 * All prompts are stored in the workflow definition (agent node + metadata).
 *
 * @param languageCode - Language code: 'vi' (Vietnamese) or 'en' (English)
 * @returns WorkflowDefinition with language-specific prompts
 */
export function getDefaultDirectQAWorkflow(languageCode: SupportedLanguage = 'vi'): WorkflowDefinition {
  const prompts = getDirectQAPrompts(languageCode);

  const nodes: WorkflowNode[] = [
    {
      id: 'start',
      type: 'start',
      position: { x: 400, y: 0 },
      data: {
        type: 'start',
        label: 'Start',
        isConfigured: true
      }
    },
    {
      id: 'direct-qa-agent',
      type: 'agent',
      position: { x: 400, y: 120 },
      data: {
        type: 'agent',
        agentType: 'direct_qa',
        label: 'Direct QA Agent',
        systemMessage: prompts.systemMessage,
        isConfigured: true,
        tools: []
      }
    },
    {
      id: 'end',
      type: 'end',
      position: { x: 400, y: 240 },
      data: {
        type: 'end',
        label: 'End',
        isConfigured: true,
        outputFormat: 'markdown'
      }
    }
  ];

  const directQaEdges: WorkflowEdge[] = [
    { id: 'e-start-agent', source: 'start', target: 'direct-qa-agent', type: 'smoothstep', animated: true },
    { id: 'e-agent-end', source: 'direct-qa-agent', target: 'end', type: 'smoothstep', animated: true }
  ];

  return {
    version: '1.0.0',
    metadata: {
      name: 'Direct QA Workflow',
      description: 'Simple workflow for direct question-answering from documents',
      welcomeMessage: prompts.welcomeMessage,
      errorMessage: prompts.errorMessage
    },
    nodes,
    edges: directQaEdges,
    viewport: { x: 0, y: 0, zoom: 0.85 }
  };
}

export default getDefaultWorkflow;
